import { forwardRef, ReactNode, ComponentType } from 'react';
import { isMobile } from '@direct-frontend/platform-code';

import { cn } from '../../../lib/css/className/className';
import { HStack, VStack } from '../../Stack';
import { SidebarNotification } from '../SidebarNotification';
import { Text } from '../../Text';
import { ColorlessIconProps } from '../../../icons';
import { useSidebarCollapsedState } from '../SidebarCollapsedStateProvider';
import { useSidebarIsNewMobileView } from '../SidebarIsNewMobileViewStateProvider';
import { SidebarItemMobile } from '../SidebarItemMobile';
import { TestProps } from '../../../internal/types/testTypes';
import { useInternalVerticalAlignWithSkin } from '../../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { SidebarItemGeneric, SidebarItemGenericProps } from '../SidebarItemGeneric/SidebarItemGeneric';

import './SidebarItem.css';

export const cnSidebarItem = cn('SidebarItem');

export interface SidebarItemContentProps
    extends Pick<SidebarItemGenericProps, 'isDisabled' | 'isTruncated'>,
        TestProps {
    /**
     * Текст пункта сайдбара
     */
    title: ReactNode;
    /**
     * Является ли заголовок жирным
     * @default true
     */
    isTitleBold?: boolean;
    /**
     * Дополнительный текст под основным
     */
    caption?: ReactNode;
    /**
     * Иконка для свёрнутого состояния
     */
    icon?: ComponentType<ColorlessIconProps>;
    /**
     * Текст элемента под иконкой в свёрнутом состоянии
     */
    iconLabel?: ReactNode;
    /**
     * Показываем ли иконку в раскрытом состоянии
     */
    alwaysShowIcons?: boolean;
    /**
     * Наличие уведомления на пункте меню
     */
    hasNotification?: boolean;
    /**
     * Количество уведомлений на пункте меню, показывается в случае переданного hasNotification={true}
     */
    notificationsCount?: number;
    /**
     * Цвет уведомления
     */
    notificationColor?: 'info' | 'error';
    /**
     * Контент, что нарисуется после title и caption
     * @internal
     */
    additionalContent?: ReactNode;
}

export const SidebarItemContent = function SidebarItemContent(props: SidebarItemContentProps) {
    const {
        'data-testid': dataTestId,
        title,
        isTitleBold = true,
        isTruncated: isTruncatedProp,
        caption,
        icon: Icon,
        iconLabel,
        hasNotification,
        notificationsCount,
        alwaysShowIcons = false,
        additionalContent,
        notificationColor = 'info',
    } = props;
    const { isCollapsed } = useSidebarCollapsedState();

    const isTruncated = !isCollapsed && isTruncatedProp;

    const { textClasses: alignTextClass, addonClasses: alignAddonClass } = useInternalVerticalAlignWithSkin({
        textSize: 'current-main + current-caption',
        addonType: 'icon',
    });

    const renderedIcon = Icon ? <Icon size="24" role="main" /> : null;

    let titleElement = (
        <Text
            bold={isTitleBold}
            isTruncated={isTruncated}
            size="s"
            className={cnSidebarItem('text', {}, [alignTextClass])}
            color="current"
        >
            {title}
        </Text>
    );

    if (hasNotification) {
        titleElement = (
            <SidebarNotification
                collapsed={isCollapsed}
                counter={notificationsCount}
                isTruncated={isTruncated}
                color={notificationColor}
            >
                {titleElement}
            </SidebarNotification>
        );
    }

    const iconElement =
        hasNotification && isCollapsed ? (
            <SidebarNotification collapsed counter={notificationsCount} color={notificationColor}>
                {renderedIcon}
            </SidebarNotification>
        ) : (
            renderedIcon
        );

    const iconElementContainer = (
        <>
            <VStack alignItems="center" justifyContent="center">
                {iconElement}
            </VStack>
            {iconLabel && (
                <div className={cnSidebarItem('iconLabel')}>
                    <Text size="2xs" bold color="current">
                        {iconLabel}
                    </Text>
                </div>
            )}
        </>
    );

    return (
        <div
            data-testid={`Sidebar.${dataTestId ?? 'SidebarItem'}`}
            className={cnSidebarItem('content', { hasLabel: Boolean(iconLabel), truncated: isTruncated })}
        >
            {isCollapsed ? (
                iconElementContainer
            ) : (
                <>
                    <HStack
                        alignItems="flex-start"
                        gap="4"
                        className={cnSidebarItem('content', { truncated: isTruncated })}
                    >
                        {alwaysShowIcons && Icon && (
                            <VStack className={cnSidebarItem('iconLeftContainer', {}, [alignAddonClass])}>
                                <Icon className={cnSidebarItem('iconLeft')} size="16" role="main" />
                            </VStack>
                        )}
                        {titleElement}
                    </HStack>
                    {caption}
                    {additionalContent}
                </>
            )}
        </div>
    );
};

export type SidebarItemProps = Omit<SidebarItemGenericProps, 'triggerProps' | 'content' | 'hasLabel'> &
    Omit<SidebarItemContentProps, 'additionalContent'>;

export const SidebarItem = forwardRef<HTMLElement, SidebarItemProps>(function SidebarItem(props, ref) {
    const {
        'data-testid': dataTestId,
        itemId,
        isActive = false,
        isBeta,
        href,
        target,
        children,
        className,
        label,
        placement,
        title,
        isTitleBold = true,
        caption,
        icon,
        iconLabel,
        hasNotification,
        notificationsCount,
        alwaysShowIcons = false,

        isDisabled,
        isTruncated: isTruncatedProp,

        ...restProps
    } = props;
    const isNewMobileView = useSidebarIsNewMobileView();

    if (isMobile() && isNewMobileView) {
        return <SidebarItemMobile {...props} ref={ref} />;
    }

    const content = (
        <SidebarItemContent
            data-testid={dataTestId}
            title={title}
            isTitleBold={isTitleBold}
            caption={caption}
            icon={icon}
            iconLabel={iconLabel}
            isDisabled={isDisabled}
            isTruncated={isTruncatedProp}
            hasNotification={hasNotification}
            notificationsCount={notificationsCount}
            alwaysShowIcons={alwaysShowIcons}
        />
    );

    return (
        <SidebarItemGeneric
            ref={ref}
            content={content}
            className={className}
            data-testid={dataTestId}
            itemId={itemId}
            isActive={isActive}
            isDisabled={isDisabled}
            isTruncated={isTruncatedProp}
            isBeta={isBeta}
            href={href}
            target={target}
            hasIconLabel={Boolean(iconLabel)}
            label={label}
            shouldContentGrow
            placement={placement}
            {...restProps}
        >
            {children}
        </SidebarItemGeneric>
    );
});
